import { useEffect, useState } from 'react';
import { logWarn } from '@ardoq/logging';
import { waitForElement } from '@ardoq/common-helpers';
import {
  isOffScreen,
  scrollAnchorIntoView,
  waitForTourAnchor,
} from './domHelpers';
import {
  RESOLVING_GRACE_MS,
  TOUR_ANCHOR_TIMEOUT_MS,
  TOUR_BEFORE_SHOW_TIMEOUT_MS,
} from './timings';
import type { TourAnchorFailureReason } from './tourTracking';
import type { ActiveTour, TourId } from './types';

const ANCHOR_FALLBACK_MESSAGE =
  '[productTour] anchor not found, showing centered fallback';

const SECONDARY_ANCHOR_MESSAGE =
  '[productTour] secondary anchor not found, showing a single highlight';

const BEFORE_SHOW_MESSAGE = '[productTour] beforeShow failed';

const remainingUntil = (deadline: number): number =>
  Math.max(deadline - performance.now(), 0);

const untilDeadline = (deadline: number): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, remainingUntil(deadline)));

type StepIdentity = {
  tourId: TourId;
  stepId: string;
};

const logAnchorFallback = (
  { tourId, stepId }: StepIdentity,
  target: string,
  reason: TourAnchorFailureReason
): void =>
  logWarn(new Error(ANCHOR_FALLBACK_MESSAGE), ANCHOR_FALLBACK_MESSAGE, {
    tourId,
    stepId,
    target,
    reason,
  });

const logSecondaryAnchorMissing = (
  { tourId, stepId }: StepIdentity,
  secondaryTarget: string
): void =>
  logWarn(new Error(SECONDARY_ANCHOR_MESSAGE), SECONDARY_ANCHOR_MESSAGE, {
    tourId,
    stepId,
    secondaryTarget,
  });

const logBeforeShowFailed = (
  identity: StepIdentity,
  target: string | null,
  error: unknown
): void =>
  logWarn(
    error instanceof Error ? error : new Error(BEFORE_SHOW_MESSAGE),
    BEFORE_SHOW_MESSAGE,
    { ...identity, target }
  );

type StepKeyArgs = {
  tourId: TourId;
  stepIndex: number;
  stepId: string | null;
};

export const toStepKey = ({ tourId, stepIndex, stepId }: StepKeyArgs): string =>
  `${tourId}:${stepIndex}:${stepId ?? ''}`;

export type StepResolution = {
  stepKey: string;
  attemptKey: string;
} & (
  | { status: 'anchored'; element: HTMLElement; secondary: HTMLElement | null }
  /** `target: null` — a centered modal on purpose. */
  | { status: 'centered' }
  /** The anchor never resolved, so the step degrades to a centered card. */
  | { status: 'fallback'; reason: TourAnchorFailureReason }
  /** Still resolving, shown centered in the meantime. */
  | { status: 'resolving' }
);

type SettledOutcome =
  | { status: 'anchored'; element: HTMLElement; secondary: null }
  | { status: 'centered' }
  | { status: 'fallback'; reason: TourAnchorFailureReason };

const isSettled = (resolution: StepResolution): boolean =>
  resolution.status === 'anchored' || resolution.status === 'centered';

export const useStepResolution = (
  activeTour: ActiveTour
): StepResolution | null => {
  const { definition, stepIndex } = activeTour;
  const tourId = definition.id;
  const step = definition.steps[stepIndex] ?? null;
  const stepKey = toStepKey({ tourId, stepIndex, stepId: step?.id ?? null });
  const dataRevision = definition.dataRevision ?? '';

  const [resolution, setResolution] = useState<StepResolution | null>(null);

  const current = resolution?.stepKey === stepKey ? resolution : null;
  const attemptKey =
    current && isSettled(current)
      ? current.attemptKey
      : `${stepKey}|${dataRevision}`;

  useEffect(() => {
    if (!step) return;
    let isCancelled = false;
    const identity: StepIdentity = { tourId, stepId: step.id };

    const graceTimer =
      step.target === null
        ? undefined
        : setTimeout(() => {
            if (isCancelled) return;
            setResolution({ status: 'resolving', stepKey, attemptKey });
          }, RESOLVING_GRACE_MS);

    const settle = (outcome: SettledOutcome) => {
      if (isCancelled) return;
      clearTimeout(graceTimer);
      setResolution({ ...outcome, stepKey, attemptKey });
    };

    const settleAnchorFailure = (
      target: string,
      reason: TourAnchorFailureReason
    ) => {
      logAnchorFallback(identity, target, reason);
      settle({ status: 'fallback', reason });
    };

    const resolveSecondary = async (secondaryTarget: string) => {
      const element = await waitForTourAnchor(secondaryTarget);
      if (isCancelled) return;
      if (!element || isOffScreen(element)) {
        logSecondaryAnchorMissing(identity, secondaryTarget);
        return;
      }
      setResolution(previous =>
        previous?.attemptKey === attemptKey && previous.status === 'anchored'
          ? { ...previous, secondary: element }
          : previous
      );
    };

    const resolve = async () => {
      const startedAt = performance.now();
      const overallDeadline = startedAt + TOUR_ANCHOR_TIMEOUT_MS;
      const beforeShowDeadline = startedAt + TOUR_BEFORE_SHOW_TIMEOUT_MS;
      try {
        await Promise.race([
          step.beforeShow?.(),
          untilDeadline(beforeShowDeadline),
        ]);
      } catch (error) {
        // Log but keep going: a failed beforeShow still attempts to anchor,
        // and the step degrades to a centered card if the anchor never appears.
        logBeforeShowFailed(identity, step.target, error);
      }
      if (isCancelled) return;
      if (step.target === null) {
        settle({ status: 'centered' });
        return;
      }
      const element = await waitForElement(
        step.target,
        remainingUntil(overallDeadline)
      );
      if (isCancelled) return;
      if (!element) {
        // Anchor never appeared — e.g. the element is conditionally rendered,
        // or Back navigated to an element that only exists on another page.
        // Degrade to a centered card (forward navigation stays possible)
        // rather than trapping or abruptly ending the tour.
        settleAnchorFailure(step.target, 'not-found');
        return;
      }
      scrollAnchorIntoView(element);
      // Guard after scrolling: if the element still isn't on-screen, fall back
      // to the centered card rather than anchoring to a rect the user can't see.
      if (isOffScreen(element)) {
        settleAnchorFailure(step.target, 'off-screen');
        return;
      }
      settle({ status: 'anchored', element, secondary: null });
      if (step.secondaryTarget) {
        resolveSecondary(step.secondaryTarget);
      }
    };

    resolve();
    return () => {
      isCancelled = true;
      clearTimeout(graceTimer);
    };
    // `attemptKey` carries both the step and the data it is resolved against,
    // which is the whole trigger. `step` is deliberately out: the tour hands out
    // a new object on every emission, and the ones that matter change the
    // revision.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptKey]);

  return current;
};
